import React, { CSSProperties, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { useChangeEffect, useClickOutside, useIsMobile } from "@utils/hooks";
import { useModalTools } from "@ui/modals/modalContext";
import { fadeIn } from "@ui/styles/keyframes";
import { desktop, mobile, TOP_BAR_OFFSET, TOP_BAR_OFFSET_MOBILE } from "@ui/styles/utils";
import { Z } from "@ui/styles/zIndex";
import { ClosableBottomSheet } from "@ui/components/closableBottomSheet";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";

export interface MenuProps extends React.HTMLAttributes<HTMLDivElement> {
	menuWrapperStyle?: CSSProperties;
	menuContentStyle?: CSSProperties;
	renderMenuContent: (close: () => void) => React.ReactNode;
	onOpenChange?: (open: boolean) => void;
	closeOnSelect?: boolean;
	position?: "top" | "bottom";
	align?: "left" | "right";
	fixed?: "right" | "center";
	bottomSheetMode?: boolean;
	bottomSheetHeight?: number;
	manualOpen?: boolean;
	menuHeight?: number | undefined;
}

export const Menu: React.FC<MenuProps> = ({
	menuWrapperStyle,
	menuContentStyle,
	renderMenuContent,
	onOpenChange,
	closeOnSelect,
	position = "bottom",
	align = "right",
	fixed,
	menuHeight = undefined,
	bottomSheetMode,
	bottomSheetHeight,
	children,
	manualOpen = false,
	...htmlProps
}) => {
	const [opened, setOpened] = useState(manualOpen);
	const [menuCenter, setMenuCenter] = useState(0);
	const { activeModalsCount } = useModalTools();
	const isMobile = useIsMobile();
	const isBottomSheet = bottomSheetMode && isMobile;

	const menuRef = useClickOutside((e) => {
		if (opened) {
			e.preventDefault();
			setOpened(false);
		}
	});

	useEffect(() => {
		if (activeModalsCount > 0) {
			setOpened(false);
		}
	}, [activeModalsCount]);

	useChangeEffect(() => {
		onOpenChange?.(opened);
	}, [opened]);

	useEffect(() => {
		const resizeListener = (): void => {
			if (!menuRef.current) {
				return;
			}
			const { left, width } = menuRef.current.getBoundingClientRect();
			setMenuCenter(left + width / 2);
		};
		resizeListener();
		window.addEventListener("resize", resizeListener);
		return () => window.removeEventListener("resize", resizeListener);
	}, []);
	return (
		<Container {...htmlProps} zIndexActive={opened && !isBottomSheet} ref={menuRef}>
			<MenuLayout
				onClick={(e) => {
					e.stopPropagation();
					setOpened((o) => !o);
				}}
			>
				{children}
			</MenuLayout>
			{isBottomSheet ? (
				<ClosableBottomSheet
					maxHeight={bottomSheetHeight}
					fitContent
					isOpen={opened}
					onClose={() => setOpened(false)}
					closeOnSelect={closeOnSelect}
				>
					<Scrollable>{renderMenuContent(() => setOpened(false))}</Scrollable>
				</ClosableBottomSheet>
			) : opened ? (
				<ContentWrapper
					menuHeight={menuHeight}
					style={menuWrapperStyle}
					position={position}
					align={align}
					fixed={fixed}
					menuCenter={menuCenter}
				>
					<MenuContent
						menuHeight={menuHeight}
						fixed={fixed}
						style={menuContentStyle}
						onClick={() => closeOnSelect && setOpened(false)}
					>
						{renderMenuContent(() => setOpened(false))}
					</MenuContent>
				</ContentWrapper>
			) : null}
		</Container>
	);
};

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "zIndexActive",
})<{ zIndexActive: boolean }>`
	position: relative;
	border-radius: 5px;
	cursor: pointer;
	${({ zIndexActive }) => zIndexActive && `z-index: ${Z.menu}`};
`;

const MenuLayout = styled.div``;

const ContentWrapper = styled.div.withConfig({
	shouldForwardProp: (prop) => !["position", "align", "fixed", "menuCenter", "menuHeight"].includes(prop),
})<{
	position: "top" | "bottom";
	align: "left" | "right";
	fixed?: "right" | "center";
	menuCenter: number;
	menuHeight?: number;
}>`
	cursor: initial;
	position: absolute;
	${({ align }) => align}: 0;
	${({ position, fixed, menuCenter, theme }) =>
		fixed
			? css`
					position: fixed;
					right: ${fixed === "right" ? 0 : `calc(100vw - ${menuCenter}px)`};
					${fixed === "center" && "transform: translate(50%);"}
					top: ${TOP_BAR_OFFSET}px;
					@media (max-width: 900px) {
						top: ${TOP_BAR_OFFSET_MOBILE}px;
						right: 0;
						left: 0;
					}
					&::after {
						position: absolute;
						content: "";
						width: 0;
						height: 0;
						right: ${fixed === "right" ? `calc(100vw - ${menuCenter}px)` : "50%"};
						top: 1px;
						border-radius: 1px;
						transform: translate(50%, -100%);
						border-left: 9px solid transparent;
						border-right: 9px solid transparent;
						border-bottom: 9px solid ${theme.colors.white};
					}
				`
			: position === "bottom"
				? css`
						bottom: 0;
						transform: translateY(100%);
					`
				: css`
						top: 0;
						transform: translateY(-100%);
					`}
	animation: ${fadeIn} 0.1s linear;
	${({ menuHeight }) => (!menuHeight ? undefined : "min-width:-webkit-fill-available")};
`;

const MenuContent = styled(RoundedBlock).withConfig({
	shouldForwardProp: (prop) => !["fixed", "menuHeight"].includes(prop),
})<{ fixed?: "center" | "right"; menuHeight?: number }>`
	padding: 12px 14px;
	box-shadow: 0 4px 66px 0 rgba(0, 0, 0, 0.24);
	overflow: auto;
	max-height: calc(100vh - ${TOP_BAR_OFFSET}px);
	max-width: 100vw;
	@media ${mobile} {
		max-height: calc(100vh - ${TOP_BAR_OFFSET_MOBILE}px);
	}
	${({ fixed }) =>
		fixed === "right" &&
		css`
			border: none;
			border-radius: 0;
			@media ${desktop} {
				border-top-left-radius: 10px;
				border-bottom-left-radius: 10px;
			}
		`}
	${({ menuHeight }) =>
		menuHeight
			? `
	max-height: ${menuHeight}px;
	overflow: auto;
	::-webkit-scrollbar {
		width: 8px;
		height: 4px;
	}
	/* Track */
	::-webkit-scrollbar-track {
		box-shadow: inset 0 0 5px grey;
		border-radius: 10px;
	}
	/* Handle */
	::-webkit-scrollbar-thumb {
		background: #007aff;
		border-radius: 10px;
	}
	/* Handle on hover */
	::-webkit-scrollbar-thumb:hover {
		background: #b30000;
	}
	`
			: undefined}
`;

const Scrollable = styled.div`
	height: 100%;
	overflow: scroll;
`;
